// Puente con el Agente de impresión RubiPOS (programa local): imprime sin ventana; si no está, cae al flujo del navegador
const URL_AGENTE = localStorage.getItem("rubipos_agente_url") || "http://127.0.0.1:9123";
let estado = { ok: false, at: 0 };

export async function agenteDisponible(force = false) {
  if (!force && Date.now() - estado.at < 15000) return estado.ok;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 700);
    const r = await fetch(`${URL_AGENTE}/salud`, { signal: ctrl.signal });
    clearTimeout(t);
    estado = { ok: r.ok, at: Date.now() };
  } catch { estado = { ok: false, at: Date.now() }; }
  return estado.ok;
}

async function enviar(ruta, body) {
  const r = await fetch(`${URL_AGENTE}${ruta}`, { method: "POST", headers: { "Content-Type": "application/json", "X-Agente-Token": localStorage.getItem("rubipos_agente_token") || "" }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Agente ${r.status}`);
  return r.json();
}

// ESC/POS: texto plano con comandos básicos (init, centrado, negritas, corte)
export const ESC = { init: "\x1b@", center: "\x1ba\x01", left: "\x1ba\x00", boldOn: "\x1bE\x01", boldOff: "\x1bE\x00", big: "\x1d!\x11", normal: "\x1d!\x00", cut: "\n\n\n\x1dV\x00", cajon: "\x1bp\x00\x19\xfa" };

/** Abre el cajón e imprime un mini ticket de retiro de efectivo (gasto/retiro) para sacar el dinero. */
export async function imprimirMiniRetiro({ sucursal, caja, monto, motivo, usuario, ancho = 80 }, fallback) {
  const n = cols(ancho);
  const texto = ESC.cajon + ESC.center + ESC.big + "RETIRO DE EFECTIVO\n" + ESC.normal + ESC.boldOn + `${sucursal || ""}\n` + ESC.boldOff + `${caja || ""}\n` + ESC.left + linea(n) + fila("Monto:", `$${Number(monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`, n) + fila("Motivo:", (motivo || "").slice(0, 24), n) + fila("Fecha:", new Date().toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }), n) + fila("Retira:", usuario || "", n) + linea(n) + "\n" + ESC.center + "______________________\nFirma\n";
  return imprimirTicket(texto, fallback);
}

/** Abre el cajón de dinero (pulso ESC/POS) e imprime un ticket corto "INICIO DE CORTE DE CAJA"; sin agente, solo muestra el ticket en el navegador. */
export async function abrirCajonInicioCorte({ sucursal, caja, apertura, usuario, ancho = 80 }, fallback) {
  const n = cols(ancho);
  const ahora = new Date();
  const f = (d) => (d ? new Date(d).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—");
  const texto = ESC.cajon + ESC.center + ESC.big + "INICIO DE CORTE\nDE CAJA\n" + ESC.normal + ESC.boldOn + `${sucursal || ""}\n` + ESC.boldOff + `${caja || ""}\n` + ESC.left + linea(n) + fila("Apertura de caja:", f(apertura), n) + fila("Inicio de corte:", f(ahora), n) + fila("Realiza:", usuario || "", n) + linea(n) + ESC.center + "Cuenta el efectivo del cajon\n";
  return imprimirTicket(texto, fallback);
}
export const cols = (ancho) => (Number(ancho) === 58 ? 32 : 48);
export const fila = (izq, der, n) => { const d = String(der); const i = String(izq).slice(0, n - d.length - 1); return `${i}${" ".repeat(Math.max(1, n - i.length - d.length))}${d}\n`; };
export const linea = (n, c = "-") => c.repeat(n) + "\n";
export const centrar = (t, n) => String(t).split("\n").map((s) => s.length >= n ? s : " ".repeat(Math.floor((n - s.length) / 2)) + s).join("\n") + "\n";

/** Imprime texto ESC/POS en la impresora de tickets; si no hay agente ejecuta `fallback()` (window.print / ventana). */
export async function imprimirTicket(texto, fallback, copias = 1) {
  try {
    if (await agenteDisponible()) {
      await enviar("/imprimir/ticket", { texto: ESC.init + texto + ESC.cut, copias });
      return "agente";
    }
  } catch { estado = { ok: false, at: Date.now() }; }
  try { await fallback?.(); } catch (e) { console.error("Impresión navegador", e); }
  return "navegador";
}

/** Imprime ZPL en la Zebra; si no hay agente ejecuta `fallback()`. */
export async function imprimirZPL(zpl, fallback) {
  if (await agenteDisponible()) { await enviar("/imprimir/etiquetas", { zpl }); return "agente"; }
  fallback?.();
  return "navegador";
}

/** Etiqueta 40×13 mm (203 dpi ≈ 320×104 dots): nombre en 2 líneas, presentación y código de barras compacto. */
export function zplEtiqueta(p) {
  const limpio = (s, n) => String(s || "").replace(/[\^~]/g, " ").slice(0, n);
  const nombre = limpio(p.nombre, 110).toUpperCase();
  // Nombres largos (feromonas, etc.): 3 líneas con letra menor para que salgan completos en 40×13 mm
  const [fh, fw, lineas, ySub] = nombre.length <= 34 ? [18, 17, 2, 44] : nombre.length <= 52 ? [15, 14, 3, 50] : [13, 12, 3, 50];
  const presentacion = p.granel ? `${p.granel.gramos} g a granel` : `${p.presentacion || ""}${p.concentracion ? ` · ${p.concentracion}` : ""}`;
  const sub = limpio(`${presentacion} · ${p.sku || ""}`, 40);
  return `^XA^PW320^LL104^LH0,0^CI28^FO6,2^FB308,${lineas},-1,L,0^A0N,${fh},${fw}^FD${nombre}^FS^FO6,${ySub}^A0N,14,13^FD${sub}^FS^FO16,66^BY1,2,28^BCN,28,N,N,N^FD${p.codigo_barras || p.sku}^FS^XZ\n`;
}
